// LandingPageModel.js
// This file will encapsulate the business logic and state for LandingPage
import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import jsonToPivotJson from "json-to-pivot-json";
import { useUploadFile } from "../hooks/useUploadFile";
import { useSqlhooks } from "../hooks/useSqlhooks";
import { useSchemaDomain } from "../hooks/useSchemaDomain";
import { useAuth } from "../hooks/useAuth";
import { useAsyncDownload } from "../hooks/useAsyncDownload";
import { StorageHelper } from "../utils/storageHelper";
import { formatSqlQuery } from "../utils/sqlFormatter";
import {
  FILTER_CONDITIONS,
  CONDITION_NAME_MAP,
  AGG_FUNCTIONS,
} from "../enum/enums";
import { useLoading } from "../contexts/LoadingContext";
import formulajs from "formulajs";
import { buildFormulaColumnNameMapping } from "../utils/formulaFunctions";

export function useLandingPageModel() {
  const { showLoading, hideLoading } = useLoading();
  const sanitizeQueryName = useCallback((value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    // Keep only A-Z, a-z, 0-9, and underscore
    const filtered = str.replace(/[^A-Za-z0-9_]/g, "");
    return filtered.slice(0, 50);
  }, []);
const getNextTaskSheetName = useCallback((existingSheets = []) => {
  let maxNum = 0;

  for (const sheet of existingSheets) {
    const name = sheet?.name;
    if (typeof name !== "string") continue;

    const match = /^Task-(\d+)$/.exec(name);
    if (!match) continue;

    const num = Number(match[1]);
    if (Number.isInteger(num) && num > maxNum && num > 0) {
      maxNum = num;
    }
  }

  return `Task-${maxNum + 1}`;
}, []);


  // State
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [data, setData] = useState([]);
  const [dragOver, setDragOver] = useState("");
  const [tableLoading, setTableLoading] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [expandedMap, setExpandedMap] = useState({});
  const [uploadedFlat, setUploadedFlat] = useState([]);
  const [uploadedHeaders, setUploadedHeaders] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [columnTypes, setColumnTypes] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [dataFilters, setDataFilters] = useState({});
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [filterMenuCol, setFilterMenuCol] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");
  const [filterOptions, setFilterOptions] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterError, setFilterError] = useState(null);
  const [filterCondition, setFilterCondition] = useState({});
  const [pendingFilter, setPendingFilter] = useState({});
  const [pendingValuesByCondition, setPendingValuesByCondition] = useState({});
  const [queryResultTableData, setQueryResultTableData] = useState(null);
  const [generatedSqlQuery, setGeneratedSqlQuery] = useState("");
  const [computedColumns, setComputedColumns] = useState([]);
  const [aggregations, setAggregations] = useState([]);
  const [aggDropdownCol, setAggDropdownCol] = useState(null);
  const [aggDropdownValue, setAggDropdownValue] = useState("");
  const [filters, setFilters] = useState([]);
  const [sqlGenError, setSqlGenError] = useState("");
  const [failedCalculatedColumn, setFailedCalculatedColumn] = useState(null); // Track failed calculated column
  const [pendingCalculatedColumn, setPendingCalculatedColumn] = useState(null); // Track pending calculated column
  const [saveQueryDialogOpen, setSaveQueryDialogOpen] = useState(false);
  const [queryName, _setQueryName] = useState("");
  const setQueryName = useCallback(
    (next) => {
      if (typeof next === "function") {
        _setQueryName((prev) => sanitizeQueryName(next(prev)));
        return;
      }
      _setQueryName(sanitizeQueryName(next));
    },
    [sanitizeQueryName]
  );
  const [saveQueryError, setSaveQueryError] = useState("");
  const [saveQuerySuccess, setSaveQuerySuccess] = useState(false);
  const [editSaveSuccessAlert, setEditSaveSuccessAlert] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [sheetNames, setSheetNames] = useState([]);

  const [modal, setModal] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
  });

  // Sheet management: start with defaults so first paint is fast; hydrate from localStorage after mount
  const getDefaultSheets = () => [
    {
      id: "sheet-1",
      name: "Task-1",
      modified: false,
      saved_query_id: null,
      owner_id: null, // For saved/shared queries: original owner user_id; null for editable tabs
      last_saved_query: "", // Stable server-saved SQL baseline for no-change checks
      data: {
        columns: [],
        rows: [],
        data: [],
        filters: [],
        aggregations: [],
        dataFilters: {},
        queryResultTableData: null,
        generatedSqlQuery: "",
        dbtype: "snowflake", // Default to snowflake for new sheets
        calculatedColumns: [], // Store calculated columns for this sheet
      },
    },
  ];

  const [sheets, setSheets] = useState(getDefaultSheets);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const sheetsHydratedFromStorage = useRef(false);

  const filterIconRefs = useRef({});
  const isInitialMount = useRef(true);
  const skipNextAutoSaveRef = useRef(false);

  // Defer heavy localStorage read until after first paint to keep initial load smooth
  useEffect(() => {
    const id = setTimeout(() => {
      try {
        const savedSheets = StorageHelper.sheets.load();
        const savedIndex = StorageHelper.activeSheet.load();
        if (savedSheets && Array.isArray(savedSheets) && savedSheets.length > 0) {
          setSheets(savedSheets);
          sheetsHydratedFromStorage.current = true;
          if (savedIndex !== null && savedIndex !== undefined) {
            const idx = parseInt(savedIndex, 10);
            if (Number.isInteger(idx) && idx >= 0 && idx < savedSheets.length) {
              setActiveSheetIndex(idx);
            }
          }
        }
      } catch (e) {
        console.warn("LandingPage: failed to load sheets from storage", e);
      }
    }, 0);
    return () => clearTimeout(id);
  }, []);

  // StorageHelper persistence functions
  const saveSheetsToStorage = (sheetsData) => {
    StorageHelper.sheets.save(sheetsData);
  };

  // Debounced save for auto-save path (heavy data) so UI stays responsive
  const saveSheetsDebounceRef = useRef({ timeoutId: null });
  const saveSheetsToStorageDebounced = useCallback((sheetsData) => {
    const ref = saveSheetsDebounceRef.current;
    if (ref.timeoutId) clearTimeout(ref.timeoutId);
    ref.timeoutId = setTimeout(() => {
      ref.timeoutId = null;
      StorageHelper.sheets.save(sheetsData);
    }, 400);
  }, []);
  useEffect(() => {
    return () => {
      if (saveSheetsDebounceRef.current.timeoutId) {
        clearTimeout(saveSheetsDebounceRef.current.timeoutId);
      }
    };
  }, []);

  const saveActiveSheetIndexToStorage = (index) => {
    StorageHelper.activeSheet.save(index);
  };

  const clearSheetsStorage = () => {
    StorageHelper.clearSheets();
  };

  const resetAllSheets = () => {
    const defaultSheets = getDefaultSheets();
    setSheets(defaultSheets);
    setActiveSheetIndex(0);
    saveSheetsToStorage(defaultSheets);
    saveActiveSheetIndexToStorage(0);

    // Reset current sheet data
    const defaultSheet = defaultSheets[0];
    setColumns(defaultSheet.data.columns);
    setRows(defaultSheet.data.rows);
    setData(defaultSheet.data.data);
    setFilters(defaultSheet.data.filters);
    setAggregations(defaultSheet.data.aggregations);
    setDataFilters(defaultSheet.data.dataFilters);
    setQueryResultTableData(defaultSheet.data.queryResultTableData);
    setGeneratedSqlQuery(defaultSheet.data.generatedSqlQuery);
    setComputedColumns(defaultSheet.data.calculatedColumns || []); // Reset calculated columns
  };

  // Hooks
  const {
    uploadFileToMinio,
    loading: uploadLoading,
    error: uploadError,
    data: uploadData,
  } = useUploadFile();
  const {
    createQueryApi,
    loading: sqlLoading,
    error: sqlError,
    data: sqlData,
    getRecordsByColumnNameApi,
    getRecordsLoading,
    getRecordsError,
    getRecordsData,
    runQueryApi,
    runQueryLoading,
    runQueryError,
    runQueryData,
    addSavedQueryApi,
    addSavedQueryLoading,
    addSavedQueryError,
    addSavedQueryData,
    updateSavedQueryApi,
    updateSavedQueryLoading,
    updateSavedQueryError,
    updateSavedQueryData,
    downloadQueryApi,
    getColumnDateDetectionApi,
    getColumnDateDetectionLoading,
    getChartMetadataApi,
    getChartMetadataLoading,
    getChartDataApi,
    getChartDataLoading,
    getChartDataError,
  } = useSqlhooks();
  const { schemaInfo } = useSchemaDomain();
  const { user } = useAuth();
  const userId = user?.userDetails?.user_id;

  // Async download functionality
  const { startAsyncDownload } = useAsyncDownload();

  // Utility functions
  function formatNumber(num) {
    return num?.toLocaleString() || "-";
  }

  // Returns headers in the order of dropped columns and data
  function getTableHeaders() {
    return [...columns.map((c) => c.name), ...data.map((d) => d.name)];
  }

  function flattenData(data, expandedMap, level = 0, parentVisible = true) {
    if (!Array.isArray(data)) return [];
    let rows = [];
    data.forEach((row) => {
      const isExpanded = !!expandedMap[row.id];
      const isVisible = parentVisible;
      rows.push({ ...row, level, isExpanded, isVisible });
      if (row.children && isExpanded) {
        rows = rows.concat(
          flattenData(row.children, expandedMap, level + 1, isVisible)
        );
      }
    });
    return rows;
  }

  function getAllColumns(
    data,
    exclude = [
      "children",
      "id",
      "ID",
      "Id",
      "isExpanded",
      "level",
      "isVisible",
      "_id",
      "row_id",
      "rowId",
    ]
  ) {
    const cols = new Set();
    function traverse(rows) {
      rows.forEach((row) => {
        Object.keys(row).forEach((key) => {
          if (!exclude.includes(key)) {
            if (
              typeof row[key] === "object" &&
              row[key] !== null &&
              !Array.isArray(row[key])
            ) {
              Object.keys(row[key]).forEach((subKey) => {
                cols.add(`${key}.${subKey}`);
              });
            } else {
              cols.add(key);
            }
          }
        });
        if (row.children) traverse(row.children);
      });
    }
    traverse(data);
    return Array.from(cols);
  }

  function getValue(row, col) {
    if (col.includes(".")) {
      const [main, sub] = col.split(".");
      return row[main]?.[sub];
    }
    return row[col];
  }

  // Helper to check if a column is numeric
  const isNumericColumn = (item) => {
    // Try to get the type from columnTypes, textDatatype, or type
    const type =
      columnTypes?.[item.name] || item.textDatatype || item.type || "";
    // Accept common numeric types
    return [
      "int",
      "integer",
      "float",
      "double",
      "decimal",
      "number",
      "numeric",
      "bigint",
      "smallint",
      "tinyint",
      "real",
    ].some((t) => type?.toLowerCase().includes(t));
  };

  // Helper to determine current db_type
  function getCurrentDbType() {
    // First check if we have a dbtype in the current sheet
    const currentSheet = sheets[activeSheetIndex];
    if (currentSheet?.data?.dbtype) {
      return currentSheet.data.dbtype;
    }

    // Then check items for database type
    const allItems = [...columns, ...rows, ...data, ...filters];
    if (
      allItems.some(
        (item) =>
          item.databaseType === "postgres" || item.db_type === "postgres"
      )
    ) {
      return "postgres";
    }

    // Check StorageHelper as fallback
    const storedDbType = StorageHelper.get("selected_user_db_type");
    if (storedDbType) {
      return storedDbType;
    }

    return "snowflake";
  }

  // File upload logic
  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map((file) => {
      let icon = null;
      if (file.name.endsWith(".pdf")) icon = "pdf";
      else if (file.name.endsWith(".zip")) icon = "zip";
      else icon = "file";
      return {
        name: file.name,
        icon,
        type: file.type,
        rawFile: file,
      };
    });
    setUploadedFiles((prev) => [...prev, ...newFiles]);
    if (files[0]) {
      const file = files[0];
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          setSheetNames(workbook.SheetNames);

          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          resolve(json);
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
      let parsedRows = [];
      let headers = [];
      if (
        data.length > 1 &&
        data[0].some((h) => h && h.toString().trim() !== "")
      ) {
        headers = data[0].map((h, idx) =>
          h && h.toString().trim() ? h : `Column${idx + 1}`
        );
        parsedRows = data
          .slice(1)
          .filter((rowArr) =>
            rowArr.some((cell) => cell !== undefined && cell !== "")
          )
          .map((rowArr) => {
            const rowObj = {};
            headers.forEach((header, idx) => {
              rowObj[header] = rowArr[idx] !== undefined ? rowArr[idx] : "";
            });
            return rowObj;
          });
      }
      setUploadedFlat(parsedRows.slice(0, 10));
      setUploadedHeaders(headers);
      setQueryResultTableData(null);
      const types = {};
      headers.forEach((h) => {
        types[h] = "VARCHAR";
      });
      setColumnTypes(types);
    }
    setUploadDialogOpen(false);
    event.target.value = null;
  };

  // Drag and drop logic
  const handleDrop = (zone, dropIndex = null) => (e) => {
    e.preventDefault();
    setDragOver("");
    let item = e.dataTransfer.getData("text/plain");
    let parsed = null;
    try {
      parsed = JSON.parse(item);
    } catch {
      parsed = null;
    }
    let dropObj = {};
    let sourceZone = null;
    let sourceIndex = null;
    if (parsed && typeof parsed === "object") {
      dropObj = { ...parsed };
      dropObj.name = parsed.name || parsed.type || "Unknown";
      dropObj.type = parsed.type || zone.slice(0, -1);
      dropObj.dbname = parsed.dbname || parsed.database || "";
      dropObj.textDatatype = parsed.textDatatype || "";
      // Determine database type based on table name pattern
      const isPostgres =
        parsed.dbname &&
        (parsed.dbname.includes("__") || // Tables with __ are Postgres
          parsed.dbname.startsWith("temp_")); // Tables starting with temp_ are Postgres
      dropObj.databaseType =
        parsed.__dbType ||
        (isPostgres ? "postgres" : parsed.databaseType || "snowflake");

      if (parsed.__sourceZone) {
        sourceZone = parsed.__sourceZone;
        delete dropObj.__sourceZone;
      }
      if (parsed.__sourceIndex !== undefined) {
        sourceIndex = parsed.__sourceIndex;
        delete dropObj.__sourceIndex;
      }
    } else if (typeof item === "string") {
      dropObj = {
        name: item,
        type: zone.slice(0, -1),
        dbname: "",
        databaseType: "snowflake",
      };
    } else {
      dropObj = {
        name: String(item),
        type: zone.slice(0, -1),
        dbname: "",
        databaseType: "snowflake",
      };
    }
    const isDuplicate = (existingItem) =>
      existingItem.name === dropObj.name &&
      existingItem.dbname === dropObj.dbname;
    // Check for database type conflicts
    const checkDatabaseTypeConflict = (existingItems) => {
      if (existingItems.length === 0) return false;
      const newItemType = dropObj.databaseType || "snowflake";
      const hasConflict = existingItems.some((item) => {
        const existingItemType = item.databaseType || "snowflake";
        return existingItemType !== newItemType;
      });
      return hasConflict;
    };
    
    // Helper function to insert item at specific index
    const insertAt = (arr, item, index) => {
      if (index === null || index === undefined || index >= arr.length) {
        return [...arr, item];
      }
      const newArr = [...arr];
      newArr.splice(index, 0, item);
      return newArr;
    };
    
    // Helper function to remove item and reinsert at new position
    const moveItem = (arr, sourceIdx, targetIdx) => {
      if (sourceIdx === null || sourceIdx === undefined || sourceIdx < 0 || sourceIdx >= arr.length) {
        return arr;
      }
      const newArr = [...arr];
      const [removed] = newArr.splice(sourceIdx, 1);
      // Adjust target index if source was before target
      let adjustedTargetIdx = targetIdx;
      if (targetIdx !== null && targetIdx !== undefined && sourceIdx < targetIdx) {
        adjustedTargetIdx = targetIdx - 1;
      }
      if (adjustedTargetIdx === null || adjustedTargetIdx === undefined || adjustedTargetIdx >= newArr.length) {
        newArr.push(removed);
      } else {
        newArr.splice(adjustedTargetIdx, 0, removed);
      }
      return newArr;
    };
    
    // Remove from source zone if moving between zones or reordering within same zone
    if (sourceZone && sourceZone === zone && sourceIndex !== null) {
      // Reordering within same zone
      if (zone === "columns") {
        if (checkDatabaseTypeConflict(columns)) {
          setModal({
            open: true,
            type: "error",
            title: "Database Type Conflict",
            message:
              "Cannot mix columns from different database types (Postgres and Snowflake).",
          });
          return;
        }
        setColumns((prev) => moveItem(prev, sourceIndex, dropIndex));
        return;
      }
      if (zone === "rows") {
        if (checkDatabaseTypeConflict(rows)) {
          setModal({
            open: true,
            type: "error",
            title: "Database Type Conflict",
            message:
              "Cannot mix columns from different database types (Postgres and Snowflake).",
          });
          return;
        }
        setRows((prev) => moveItem(prev, sourceIndex, dropIndex));
        return;
      }
      if (zone === "data") {
        if (checkDatabaseTypeConflict(data)) {
          setModal({
            open: true,
            type: "error",
            title: "Database Type Conflict",
            message:
              "Cannot mix columns from different database types (Postgres and Snowflake).",
          });
          return;
        }
        setData((prev) => moveItem(prev, sourceIndex, dropIndex));
        return;
      }
      if (zone === "filters") {
        if (checkDatabaseTypeConflict(filters)) {
          setModal({
            open: true,
            type: "error",
            title: "Database Type Conflict",
            message:
              "Cannot mix columns from different database types (Postgres and Snowflake).",
          });
          return;
        }
        setFilters((prev) => moveItem(prev, sourceIndex, dropIndex));
        return;
      }
    } else if (sourceZone && sourceZone !== zone) {
      // Moving between zones
      if (sourceZone === "columns")
        setColumns((prev) => prev.filter((i) => !isDuplicate(i)));
      if (sourceZone === "rows")
        setRows((prev) => prev.filter((i) => !isDuplicate(i)));
      if (sourceZone === "data") {
        setData((prev) => prev.filter((i) => !isDuplicate(i)));
        // Remove any aggregations for this column when moving out of data zone
        setAggregations((prev) =>
          prev.filter((agg) => agg.columnName !== dropObj.name)
        );
      }
      if (sourceZone === "filters") {
        setFilters((prev) => prev.filter((i) => !isDuplicate(i)));
        // Remove all filters for this column from dataFilters
        setDataFilters((prev) => {
          const newFilters = { ...prev };
          delete newFilters[dropObj.name];
          return newFilters;
        });
        // Remove any pending values for this column from pendingValuesByCondition
        setPendingValuesByCondition((prev) => {
          const newPending = { ...prev };
          Object.keys(newPending).forEach((key) => {
            if (key.startsWith(dropObj.name + "||") || key === dropObj.name) {
              delete newPending[key];
            }
          });
          return newPending;
        });
      }
    }
    if (zone === "columns") {
      if (checkDatabaseTypeConflict(columns)) {
        setModal({
          open: true,
          type: "error",
          title: "Database Type Conflict",
          message:
            "Cannot mix columns from different database types (Postgres and Snowflake).",
        });
        return;
      }
      setColumns((prev) => {
        if (prev.some(isDuplicate)) return prev;
        return insertAt(prev, dropObj, dropIndex);
      });
    }
    if (zone === "rows") {
      if (checkDatabaseTypeConflict(rows)) {
        setModal({
          open: true,
          type: "error",
          title: "Database Type Conflict",
          message:
            "Cannot mix columns from different database types (Postgres and Snowflake).",
        });
        return;
      }
      setRows((prev) => {
        if (prev.some(isDuplicate)) return prev;
        return insertAt(prev, dropObj, dropIndex);
      });
    }
    if (zone === "data") {
      if (!isNumericColumn(dropObj)) {
        return;
      }
      if (checkDatabaseTypeConflict(data)) {
        setModal({
          open: true,
          type: "error",
          title: "Database Type Conflict",
          message:
            "Cannot mix columns from different database types (Postgres and Snowflake).",
        });
        return;
      }
      setData((prev) => {
        if (prev.some(isDuplicate)) return prev;
        return insertAt(prev, dropObj, dropIndex);
      });
      // Auto-set SUM aggregation for numeric columns if not already set
      setAggregations((prev) => {
        const already = prev.some(
          (agg) => agg.columnName === dropObj.name && agg.fn === "SUM"
        );
        if (!already) {
          return [...prev, { fn: "SUM", columnName: dropObj.name }];
        }
        return prev;
      });
    }
    if (zone === "filters") {
      if (checkDatabaseTypeConflict(filters)) {
        setModal({
          open: true,
          type: "error",
          title: "Database Type Conflict",
          message:
            "Cannot mix columns from different database types (Postgres and Snowflake).",
        });
        return;
      }
      setFilters((prev) => {
        if (prev.some(isDuplicate)) return prev;
        return insertAt(prev, dropObj, dropIndex);
      });
    }
  };

  useEffect(() => {
    setAggregations((prev) => {
      let newAggs = [...prev];
      data.forEach((col) => {
        if (
          isNumericColumn(col) &&
          // Only auto-add a default aggregation if this column
          // doesn't already have any aggregation configured.
          !newAggs.some((agg) => agg.columnName === col.name)
        ) {
          newAggs.push({ fn: "SUM", columnName: col.name });
        }
      });
      return newAggs;
    });
    // Only run when data changes
  }, [data]);

  // Table data loading effect (mocked for now)
  useEffect(() => {
    setTableLoading(true);
    const timer = setTimeout(() => {
      setTableData([]); // Replace with actual data logic
      setTableLoading(false);
    }, 1800);
    return () => clearTimeout(timer);
  }, []);

  // Move handleGenerateSQL from LandingPage.js
  const handleGenerateSQL = async (overrideComputedColumns = null, pendingColumn = null) => {
    showLoading();
    setSqlGenError("");
    try {
      // Identify which columns are present in Columns/Rows/Data for each table
      const allItems = [...columns, ...rows, ...data];
      // Add filter items that are not already in allItems
      const allFilterItems = filters.filter(
        (f) =>
          !allItems.some(
            (item) => item.name === f.name && item.dbname === f.dbname
          )
      );
      const allRelevantItems = [...allItems, ...allFilterItems];
      const tableNameArray = allRelevantItems
        .map((item) => item.dbname)
        .filter(Boolean);
      const tableNameSet = new Set(tableNameArray);
      const arr = Array.from(tableNameSet);
      let FinalArrayList = [];

      // Get current sheet and prepare calculated columns from storage
      const currentSheet = sheets[activeSheetIndex];
      // Priority: 1. overrideComputedColumns (for immediate updates - even if empty array), 2. computedColumns state, 3. active sheet's calculatedColumns from storage
      let columnsToUse = null;
      if (
        overrideComputedColumns !== null &&
        Array.isArray(overrideComputedColumns)
      ) {
        // Use overrideComputedColumns even if it's an empty array (explicit override)
        columnsToUse = overrideComputedColumns;
      } else if (Array.isArray(computedColumns) && computedColumns.length > 0) {
        columnsToUse = computedColumns;
      } else if (
        currentSheet?.data?.calculatedColumns &&
        Array.isArray(currentSheet.data.calculatedColumns) &&
        currentSheet.data.calculatedColumns.length > 0
      ) {
        // Fallback to active sheet's calculatedColumns from storage
        columnsToUse = currentSheet.data.calculatedColumns;
      }
      // Ensure columnsToUse is always an array
      const safeColumnsToUse = Array.isArray(columnsToUse) ? columnsToUse : [];

      // Determine database type (postgres or snowflake)
      const currentDbType = getCurrentDbType();
      const isPostgres = currentDbType === "postgres";

      // Helper to infer datatype for a calculated column from underlying columns
      const inferCalcDataType = (calcCol) => {
        if (!calcCol || !calcCol.formula) return null;
        const formulaBody = calcCol.formula.trim().replace(/^=/, "");
        if (!formulaBody) return null;

        // Look at all items from the same table, and see which column names
        // actually appear in the formula; then use that column's textDatatype.
        const tableItems = allItems.filter(
          (item) => item.dbname === calcCol.tableName
        );
        for (const item of tableItems) {
          const name = (item.name || "").trim();
          if (!name) continue;
          const sanitized = name.replace(/\s+/g, "_");
          const re = new RegExp(`\\b${sanitized}\\b`, "i");
          if (re.test(formulaBody)) {
            const itemType = item.textDatatype || item.type || null;
            if (itemType) return itemType;
          }
        }
        return null;
      };

      // Map calculated columns to the format needed for calculated_columns
      const calculatedColumnsForQuery = safeColumnsToUse.map((col) => {
        const baseColumn = {
          columnName: col.columnName || "",
          formula: col.formula || "",
          tableName: col.tableName || "",
        };

        // Build formulaColumnName mapping using helper function
        // Gets original column names from stored schema data
        if (col.formula) {
          const formulaColumnName = buildFormulaColumnNameMapping(
            col.formula,
            col.tableName,
            isPostgres
          );

          if (formulaColumnName.length > 0) {
            baseColumn.formulaColumnName = formulaColumnName;
          }
        }

        return baseColumn;
      });

      // Debug: verify calculated column datatypes being sent to backend
          console.log("Calculated columns for query payload:", calculatedColumnsForQuery);

      // Normalize dataFilters keys to original case and trimmed
      const normalizedDataFilters = {};
      Object.keys(dataFilters).forEach((k) => {
        normalizedDataFilters[k.trim()] = dataFilters[k];
      });

      arr.forEach((tablename) => {
        // Columns for this table (from columns, rows, data ONLY)
        let tableColumns = allItems
          .filter((item) => item.dbname === tablename)
          .map((item) => (item.name || "").trim());

        // Use only aggregations for columns in this table
        const tableAggregations = aggregations
          .filter((agg) => tableColumns.includes(agg.columnName))
          .map((agg) => ({
            fn: agg.fn,
            columnName: agg.columnName,
            tableName: tablename,
          }));

        // If there are aggregations, only include non-aggregated columns in columns (for group by)
        let finalColumns = tableColumns;
        if (tableAggregations.length > 0) {
          const aggCols = tableAggregations.map((a) => a.columnName);
          finalColumns = tableColumns.filter((col) => !aggCols.includes(col));
        }

        // Group and map conditions (include all dataFilters for this table, regardless of dropped zones)
        const grouped = {};
        // Find all columns for this table that have filters (by checking filters or allRelevantItems for dbname match)
        const tableFilterColumns = Object.keys(dataFilters).filter((col) => {
          // Find the dbname for this filter column (from filters zone or allRelevantItems)
          let filterItem =
            filters.find((f) => f.name === col && f.dbname === tablename) ||
            allRelevantItems.find(
              (f) => f.name === col && f.dbname === tablename
            );
          return !!filterItem;
        });
        // For each filter column, add all its filter conditions
        tableFilterColumns.forEach((col) => {
          if (Array.isArray(dataFilters[col])) {
            dataFilters[col].forEach((filterObj) => {
              if (filterObj && filterObj.condition) {
                const backendCond =
                  CONDITION_NAME_MAP[filterObj.condition] ||
                  filterObj.condition;
                const key = col + "||" + backendCond;
                if (!grouped[key]) {
                  grouped[key] = {
                    conditionValue: [],
                    conditionName: backendCond,
                    columnName: col,
                  };
                }
                if (Array.isArray(filterObj.value)) {
                  grouped[key].conditionValue.push(...filterObj.value);
                } else if (filterObj.value !== undefined) {
                  grouped[key].conditionValue.push(filterObj.value);
                } else if (["IS NULL", "IS NOT NULL"].includes(backendCond)) {
                  // For null/notnull, no value needed
                }
              }
            });
          }
        });
        const conditions = Object.values(grouped);

        // filterlist: all filter column names for this table that have active filters
        const filterlist = tableFilterColumns;

        // Warn if conditions is empty but filters exist
        if (
          conditions.length === 0 &&
          tableColumns.some((col) => dataFilters[col])
        ) {
          alert(
            "Warning: Filters exist but were not matched to columns. Please check column names."
          );
        }

        // Use schemaInfo for database/schema
        const database = schemaInfo?.org_name || "";
        const schema = schemaInfo?.schema_name || "";

        FinalArrayList.push({
          operation: "select",
          table: tablename,
          columns: finalColumns,
          conditions,
          aggregations: tableAggregations,
          filterlist,
          database,
          schema,
          calculated_columns:
            calculatedColumnsForQuery.length > 0
              ? calculatedColumnsForQuery
              : [],
        });
      });

      // Joins (mocked as empty for now)
      const joins = [];

      // Get userDbId from StorageHelper
      let userDbId =
        StorageHelper.get("snowflake_datasource_id") ||
        StorageHelper.get("selected_user_db_id");

      // Check if all tables are uploaded (Postgres) tables
      const allTablesAreUploaded = FinalArrayList.every(
        (item) => item.table && item.table.startsWith("temp_")
      );

      // For uploaded tables, use user_db_id = 0
      if (allTablesAreUploaded) {
        userDbId = "0";
        console.log("All tables are uploaded tables, using user_db_id = 0");
      }

      // Get dbtype from current sheet first
      const dbType =
        currentSheet?.data?.dbtype ||
        currentSheet?.data?.columns?.[0]?.databaseType ||
        currentSheet?.data?.columns?.[0]?.__dbType ||
        getCurrentDbType();

      const result = await createQueryApi(
        FinalArrayList,
        joins,
        schemaInfo?.org_name || "",
        schemaInfo?.schema_name || "",
        String(userId),
        String(userDbId)
      );
      if (result.success) {
        const generated = result.data.generatedQueries?.[0];
        const sql = generated;

        // Store the generated SQL query, formatted for the SQL editor
        setGeneratedSqlQuery(sql ? formatSqlQuery(sql) : "");

        // Now call runQueryApi with the generated SQL
        if (sql) {
          // Prepare the SQLQueryInput object
          const sqlQuery = { sql };
          const rowFields = rows.map((r) => r.name).filter(Boolean);
          const columnFields = columns.map((c) => c.name).filter(Boolean);

          // CRITICAL FIX: Use safeColumnsToUse (which respects overrideComputedColumns) instead of computedColumns state
          // This ensures dataFields includes the pending/new calculated column
          const dataFields = [
            ...data.map((d) => d.name).filter(Boolean),
            ...(safeColumnsToUse || [])
              .map((cc) => cc.columnName)
              .filter(Boolean),
          ];

          const runResult = await runQueryApi(
            sqlQuery,
            Number(userId),
            Number(userDbId),
            columnFields,
            rowFields,
            dataFields,
            dbType // <-- pass db_type here
          );
          if (runResult.success) {
            const output = transformPivotTableData(runResult.data);
            setQueryResultTableData(output);
            setUploadedFlat([]); // Clear uploaded file data when showing SQL data
            setUploadedHeaders([]);

            // Persist latest result into the active sheet immediately so the table updates
            // without requiring a tab switch (sheet sync effect).
            setSheets((prevSheets) => {
              if (!Array.isArray(prevSheets) || prevSheets.length === 0) return prevSheets;
              if (activeSheetIndex < 0 || activeSheetIndex >= prevSheets.length) return prevSheets;
              const next = prevSheets.map((s, idx) => {
                if (idx !== activeSheetIndex) return s;
                return {
                  ...s,
                  data: {
                    ...(s.data || {}),
                    queryResultTableData: output,
                    generatedSqlQuery: sql ? formatSqlQuery(sql) : (s.data?.generatedSqlQuery || ""),
                  },
                };
              });
              saveSheetsToStorageDebounced(next);
              return next;
            });

            // SUCCESS: If there's a pending calculated column, ensure computedColumns is consistent
            const columnToApply = pendingColumn || pendingCalculatedColumn;
            if (columnToApply) {
              if (columnToApply.__isUpdate) {
                setComputedColumns((prev) => {
                  const prevArray = Array.isArray(prev) ? prev : [];
                  const updated = prevArray.map((col) => {
                    if (col.columnName === columnToApply.__oldColumnName) {
                      return {
                        columnName: columnToApply.columnName,
                        formula: columnToApply.formula,
                        tableName: columnToApply.tableName,
                      };
                    }
                    return col;
                  });
                  return updated;
                });
              } else {
                setComputedColumns((prev) => {
                  const prevArray = Array.isArray(prev) ? prev : [];
                  const already = prevArray.some(
                    (c) => c?.columnName === columnToApply.columnName
                  );
                  return already ? prevArray : [...prevArray, columnToApply];
                });
              }
              setPendingCalculatedColumn(null); // Clear pending state
            }
            setFailedCalculatedColumn(null);
            setSqlGenError("");
          } else {
            const errorMsg = "Query Run Failed: " + (runResult.error || runResult.message || "Unknown error");
            setSqlGenError(errorMsg);

            // FAILURE: If there's a pending calculated column, mark it as failed
            const columnToFail = pendingColumn || pendingCalculatedColumn;
            if (columnToFail) {
              const failedCol = {
                ...columnToFail,
                errorMessage: errorMsg
              };
              setFailedCalculatedColumn(failedCol);
              setPendingCalculatedColumn(null); // Clear pending state

              // Roll back optimistic add so zones don't show invalid calculated column
              setComputedColumns((prev) => {
                const prevArray = Array.isArray(prev) ? prev : [];
                const filtered = prevArray.filter(
                  (c) => c?.columnName !== columnToFail.columnName
                );

                const currentSheet = sheets[activeSheetIndex];
                if (currentSheet) {
                  const updatedSheets = sheets.map((sheet, index) =>
                    index === activeSheetIndex
                      ? {
                          ...sheet,
                          data: {
                            ...(sheet.data || {}),
                            calculatedColumns: filtered,
                          },
                          modified: true,
                        }
                      : sheet
                  );
                  setSheets(updatedSheets);
                  saveSheetsToStorage(updatedSheets);
                }

                return filtered;
              });
            }
          }
        }
      } else {
        const errorMsg = result.error || "SQL Generation Failed";
        setSqlGenError(errorMsg);

        // FAILURE: If there's a pending calculated column, mark it as failed
        const columnToFail = pendingColumn || pendingCalculatedColumn;
        if (columnToFail) {
          const failedCol = {
            ...columnToFail,
            errorMessage: errorMsg
          };
          setFailedCalculatedColumn(failedCol);
          setPendingCalculatedColumn(null); // Clear pending state

          // Roll back optimistic add so zones don't show invalid calculated column
          setComputedColumns((prev) => {
            const prevArray = Array.isArray(prev) ? prev : [];
            const filtered = prevArray.filter(
              (c) => c?.columnName !== columnToFail.columnName
            );

            const currentSheet = sheets[activeSheetIndex];
            if (currentSheet) {
              const updatedSheets = sheets.map((sheet, index) =>
                index === activeSheetIndex
                  ? {
                      ...sheet,
                      data: {
                        ...(sheet.data || {}),
                        calculatedColumns: filtered,
                      },
                      modified: true,
                    }
                  : sheet
              );
              setSheets(updatedSheets);
              saveSheetsToStorage(updatedSheets);
            }

            return filtered;
          });
        }
      }
    } catch (err) {
      const errorMsg = err.message || "SQL Generation Failed";
      setSqlGenError(errorMsg);

      // FAILURE: If there's a pending calculated column, mark it as failed
      const columnToFail = pendingColumn || pendingCalculatedColumn;
      if (columnToFail) {
        const failedCol = {
          ...columnToFail,
          errorMessage: errorMsg
        };
        setFailedCalculatedColumn(failedCol);
        setPendingCalculatedColumn(null); // Clear pending state

        // Roll back optimistic add so zones don't show invalid calculated column
        setComputedColumns((prev) => {
          const prevArray = Array.isArray(prev) ? prev : [];
          const filtered = prevArray.filter(
            (c) => c?.columnName !== columnToFail.columnName
          );

          const currentSheet = sheets[activeSheetIndex];
          if (currentSheet) {
            const updatedSheets = sheets.map((sheet, index) =>
              index === activeSheetIndex
                ? {
                    ...sheet,
                    data: {
                      ...(sheet.data || {}),
                      calculatedColumns: filtered,
                    },
                    modified: true,
                  }
                : sheet
            );
            setSheets(updatedSheets);
            saveSheetsToStorage(updatedSheets);
          }

          return filtered;
        });
      }
    } finally {
      hideLoading();
    }
  };

  // Function to transform pivot table data into readable format
  function transformPivotTableData(data) {
    // Always return a NEW array reference so the table re-renders immediately
    // even if the backend reuses array/object references internally.
    if (!Array.isArray(data) || data.length === 0) return [];
    return data.map((row) => ({ ...(row || {}) }));
  }

  // Function to execute custom SQL query
  // pastedUserDbId and pastedDbType are optional params from pasted JSON in SqlEditor
  const executeCustomSql = async (customSql, pastedUserDbId = null, pastedDbType = null) => {
    // Always return the same shape expected by SqlEditor.handleExecute
    const fail = (msg) => ({
      data: {
        runQuery: {
          success: false,
          message: msg || "Unknown error",
          data: null,
        },
      },
    });
    const ok = (payload) => ({
      data: { runQuery: { success: true, message: "OK", ...payload } },
    });

    if (!customSql || !customSql.trim()) {
      // Don't alert; return a proper failure for the Modal to render
      return fail("Please enter a valid SQL query.");
    }

    try {
      const sqlQuery = { sql: customSql };

      // Get current sheet data
      const currentSheet = sheets[activeSheetIndex];
      const currentData = currentSheet?.data || {};

      // Use either current state or sheet data, preferring state if available
      const rowFields = (rows.length > 0 ? rows : currentData.rows || [])
        .map((r) => r.name)
        .filter(Boolean);
      const columnFields = (
        columns.length > 0 ? columns : currentData.columns || []
      )
        .map((c) => c.name)
        .filter(Boolean);
      const dataFields = [
        ...(data.length > 0 ? data : currentData.data || [])
          .map((d) => d.name)
          .filter(Boolean),
        ...(computedColumns || []).map((cc) => cc.columnName).filter(Boolean),
      ];

      // Use pastedDbType if provided, otherwise get from current sheet
      const dbType = pastedDbType !== null
        ? pastedDbType
        : (currentSheet?.data?.dbtype ||
           currentSheet?.data?.columns?.[0]?.databaseType ||
           currentSheet?.data?.columns?.[0]?.__dbType ||
           getCurrentDbType());

      // Use pastedUserDbId if provided, otherwise fall back to stored value.
      // For postgres, backend expects 0.
      const userDbId =
        pastedUserDbId !== null && (dbType === "postgres" || String(pastedUserDbId) !== "0")
          ? pastedUserDbId
          : (dbType === "postgres"
              ? 0
              : (StorageHelper.get("snowflake_datasource_id") ??
                  StorageHelper.get("selected_user_db_id")));

      console.log("Executing SQL with:", {
        rowFields,
        columnFields,
        dataFields,
        dbType,
        userDbId,
        sql: sqlQuery.sql,
      });

      const runResult = await runQueryApi(
        sqlQuery,
        Number(userId),
        Number(userDbId),
        columnFields,
        rowFields,
        dataFields,
        dbType,
        false
      );

      if (runResult?.success) {
        // keep your side effects
        const output = transformPivotTableData(runResult.data);
        setQueryResultTableData(output);
        setUploadedFlat([]);
        setUploadedHeaders([]);
        if (runResult.executedSQL) {
          // Keep the raw SQL for execution, but store a formatted version for the editor
          setGeneratedSqlQuery(formatSqlQuery(runResult.executedSQL));
        }

        // Persist latest result into the active sheet immediately
        setSheets((prevSheets) => {
          if (!Array.isArray(prevSheets) || prevSheets.length === 0) return prevSheets;
          if (activeSheetIndex < 0 || activeSheetIndex >= prevSheets.length) return prevSheets;
          const next = prevSheets.map((s, idx) => {
            if (idx !== activeSheetIndex) return s;
            return {
              ...s,
              data: {
                ...(s.data || {}),
                queryResultTableData: output,
                generatedSqlQuery: runResult.executedSQL
                  ? formatSqlQuery(runResult.executedSQL)
                  : (s.data?.generatedSqlQuery || ""),
              },
            };
          });
          saveSheetsToStorageDebounced(next);
          return next;
        });

        // return success in the editor-friendly shape
        return ok({ data: runResult.data, executedSQL: runResult.executedSQL });
      }

      // IMPORTANT: return the backend message so the editor can parse "line N"
      // Make sure your backend puts "syntax error line X ..." into runResult.error or .message
      const backendMsg =
        runResult?.error || runResult?.message || "Query failed.";
      return fail(backendMsg);
    } catch (error) {
      // Network/transport error — still return the same shape
      return fail(error?.message || "Network error while running query.");
    }
  };

  // Function to handle save query
  const handleSaveQuery = (queryData, saveAs) => {
    if (!generatedSqlQuery || !generatedSqlQuery.trim()) {
      setModal({
        open: true,
        type: "error",
        title: "No Query",
        message: "No SQL query to save. Please generate a query first.",
      });
      return;
    }
    // else if (queryData?.query === generatedSqlQuery) {
    //   setModal({
    //     open: true,
    //     type: "error",
    //     title: "No Changes in Query",
    //     message:
    //       "The SQL query has no changes. Please generate the query again before saving.",
    //   });
    //   return;
    // }
    else if (queryData?.saved_query_id && !saveAs) {
      // Prevent updating a shared query (owner mismatch).
      const sheetOwnerId = queryData?.owner_id ?? null;
      if (
        sheetOwnerId !== null &&
        sheetOwnerId !== undefined &&
        userId != null &&
        !Number.isNaN(Number(userId)) &&
        Number(sheetOwnerId) !== Number(userId)
      ) {
        setModal({
          open: true,
          type: "error",
          title: "Shared Query",
          message:
            "You can’t update a shared query. Use “Save As” to create your own copy.",
        });
        return;
      }

      // Update existing saved query
      setQueryName(`${queryData?.name || ""}`);
      setSaveQueryError("");
      setSaveQuerySuccess(false);
      saveQuery(true, queryData?.saved_query_id);
      return;
    }

    // For new tabs or when saveAs is true, always create a new query
    // Auto-populate query name with current tab name
    const currentSheet = sheets[activeSheetIndex];
    const currentTabName = currentSheet?.name || "";

    setSaveQueryDialogOpen(true);
    setQueryName(currentTabName);
    setSaveQueryError("");
    setSaveQuerySuccess(false);
  };

  const saveQuery = async (editMode = false, editQueryId = false) => {
    const safeQueryName = sanitizeQueryName(queryName);
    if (!safeQueryName) {
      setSaveQueryError("Please enter a query name");
      return;
    }
    if (!generatedSqlQuery || !generatedSqlQuery.trim()) {
      setSaveQueryError("No SQL query to save");
      return;
    }
    try {
      const dbtype = getCurrentDbType();
      const userDbId = dbtype === "postgres" ? 0 : StorageHelper.get("snowflake_datasource_id");
      const scenerioJson = {
        columns: [...columns],
        rows: [...rows],
        data: [...data],
        filters: [...filters],
        // IMPORTANT: filter "conditions" are stored here (not in `filters`)
        dataFilters: { ...(dataFilters || {}) },
        aggregations: [...aggregations],
        // Persist calculated columns per sheet so they restore properly
        calculatedColumns: [...(computedColumns || [])],
        dbtype: dbtype,
        // Persist the last rendered table so updating a saved query
        // does not clear the UI table data immediately.
        queryResultTableData: queryResultTableData,
        // Also persist the exact generated SQL shown in the editor.
        generatedSqlQuery: generatedSqlQuery || "",
      };
      let result;
      if (editMode && editQueryId) {
        result = await updateSavedQueryApi(
          editQueryId,
          userId,
          generatedSqlQuery.trim(),
          scenerioJson
        );
      } else {
        result = await addSavedQueryApi(
          userId,
          userDbId,
          safeQueryName,
          generatedSqlQuery.trim(),
          scenerioJson
        );
      }
      if (result.success) {
        // Update the active tab name to the saved query name and mark as saved query
        // Important: keep the latest table state in `sheet.data` too.
        // Otherwise, the effect that re-syncs UI from `sheet.data` on `sheets` change
        // can temporarily clear the table after clicking Save.
        const newSheets = sheets.map((sheet, index) => {
          if (index !== activeSheetIndex) return sheet;
          return {
            ...sheet,
            name: safeQueryName,
            modified: false,
            saved_query_id: result.data?.saved_query_id,
                owner_id: userId != null ? Number(userId) : null,
                last_saved_query: generatedSqlQuery.trim(),
            data: {
              ...(sheet.data || {}),
              columns,
              rows,
              data,
              filters,
              aggregations,
              dataFilters,
              queryResultTableData,
              generatedSqlQuery,
              calculatedColumns: computedColumns || [],
            },
          };
        });
        setSheets(newSheets);
        saveSheetsToStorage(newSheets);

        if (editMode && editQueryId) {
          setEditSaveSuccessAlert(true);
          setTimeout(() => setEditSaveSuccessAlert(false), 2000);
        } else {
          setSaveQuerySuccess(true);
          setTimeout(() => {
            setSaveQueryDialogOpen(false);
            setQueryName("");
            setSaveQueryError("");
            setSaveQuerySuccess(false);
          }, 1500);
        }
      } else {
        setSaveQueryError(result.error || "Failed to save query");
      }
    } catch (error) {
      setSaveQueryError(error.message || "Failed to save query");
    }
  };

  // Function to close save query dialog
  const closeSaveQueryDialog = () => {
    setSaveQueryDialogOpen(false);
    setQueryName("");
    setSaveQueryError("");
    setSaveQuerySuccess(false);
  };

  // Sheet management functions
  const addSheet = () => {
    const newSheet = {
      id: `sheet-${Date.now()}`,
      name: getNextTaskSheetName(sheets),
      modified: false,
      saved_query_id: null,
      owner_id: null,
      last_saved_query: "",
      data: {
        columns: [],
        rows: [],
        data: [],
        filters: [],
        aggregations: [],
        dataFilters: {},
        queryResultTableData: null,
        generatedSqlQuery: "",
        dbtype: getCurrentDbType(), // Use current dbtype for new sheets
        calculatedColumns: [], // Store calculated columns for this sheet
      },
    };
    // Add the new sheet at the beginning
    const newSheets = [newSheet, ...sheets];
    setSheets(newSheets);
    setActiveSheetIndex(0); // Set active index to the new sheet
    saveSheetsToStorage(newSheets);
    saveActiveSheetIndexToStorage(0);

    // Clear all data for the new tab
    setColumns([]);
    setRows([]);
    setData([]);
    setFilters([]);
    setAggregations([]);
    setDataFilters({});
    setQueryResultTableData(null);
    setGeneratedSqlQuery("");
    setComputedColumns([]); // Clear calculated columns for new sheet
    setUploadedFlat([]);
    setUploadedHeaders([]);
  };

  const addSheetWithData = useCallback(
    async (sheetData, sheetName, savedQueryId = null) => {
      const ownerId = sheetData?.owner_id ?? null;
      const lastSavedQuery = String(sheetData?.generatedSqlQuery || "").trim();
      // Limit base name to 50 characters
      const baseName = (sheetName || `Query-${Date.now()}`).substring(0, 50);

      // Use storage when available so we see all existing tabs even if hydration hasn't run yet.
      // (Opening a saved query often runs before the deferred hydration from storage.)
      let existingSheets = sheets;
      try {
        const stored = StorageHelper.sheets.load();
        if (stored && Array.isArray(stored) && stored.length > 0) {
          existingSheets = stored;
        }
      } catch (e) {
        // keep existingSheets = sheets
      }

      // If a tab with the same name is already open, reuse it instead of creating a new tab.
      const existingIndex = existingSheets.findIndex(
        (s) =>
          typeof s?.name === "string" &&
          s.name.trim() === baseName.trim()
      );

      // Build set of existing sheet names (case-insensitive) to ensure uniqueness
      // for the "create new tab" path.
      const existingLower = new Set(
        existingSheets
          .map((s) => (typeof s?.name === "string" ? s.name : null))
          .filter((n) => n)
          .map((n) => n.trim().toLowerCase())
      );

      const makeUniqueName = (desired) => {
        const desiredTrimmed = desired.trim();
        const desiredLower = desiredTrimmed.toLowerCase();
        if (!existingLower.has(desiredLower)) return desiredTrimmed;

        const m = /^(.*?)(?:\s*\((\d+)\))?$/.exec(desiredTrimmed);
        const baseRaw = (m?.[1] || desiredTrimmed).trim();

        let i = 1;
        while (true) {
          const suffix = ` (${i})`;
          const maxBaseLen = Math.max(1, 50 - suffix.length);
          const base = baseRaw.substring(0, maxBaseLen).trimEnd();
          const candidate = `${base}${suffix}`;
          if (!existingLower.has(candidate.toLowerCase())) return candidate;
          i += 1;
        }
      };

      const targetName = existingIndex !== -1 ? baseName : makeUniqueName(baseName);

      // Preserve the dbtype from the incoming data
      const dbtype =
        sheetData.dbtype ||
        sheetData.columns?.[0]?.databaseType ||
        sheetData.columns?.[0]?.__dbType ||
        StorageHelper.get("selected_user_db_type") ||
        "snowflake";

      const newSheetData = {
        // Preserve user_db_id from saved query navigation so auto-execute can target the correct DB
        user_db_id: sheetData.user_db_id ?? sheetData.userDbId ?? null,
        columns: sheetData.columns || [],
        rows: sheetData.rows || [],
        data: sheetData.data || [],
        filters: sheetData.filters || [],
        aggregations: sheetData.aggregations || [],
        dataFilters: sheetData.dataFilters || {},
        queryResultTableData: sheetData.queryResultTableData || null,
        generatedSqlQuery: sheetData.generatedSqlQuery || "",
        dbtype: dbtype,
        calculatedColumns: sheetData.calculatedColumns || [], // Preserve calculated columns
      };

      let newSheets;
      let targetIndex;

      if (existingIndex !== -1) {
        // Reuse the existing tab: update its data and activate it.
        newSheets = existingSheets.map((sheet, idx) =>
          idx === existingIndex
            ? {
                ...sheet,
                name: targetName,
                data: newSheetData,
                saved_query_id: savedQueryId,
                owner_id: ownerId,
                last_saved_query: lastSavedQuery,
                modified: false,
              }
            : sheet
        );
        targetIndex = existingIndex;
      } else {
        // No tab with this name exists yet: create a new sheet and prepend.
        const newSheet = {
          id: `sheet-${Date.now()}`,
          name: targetName,
          modified: false,
          saved_query_id: savedQueryId,
          owner_id: ownerId,
          last_saved_query: lastSavedQuery,
          data: newSheetData,
        };
        newSheets = [newSheet, ...existingSheets];
        targetIndex = 0;
      }

      setSheets(newSheets);
      setActiveSheetIndex(targetIndex);
      saveSheetsToStorage(newSheets);
      saveActiveSheetIndexToStorage(targetIndex);

      // First update the state with the new data
      setColumns(newSheetData.columns || []);
      setRows(newSheetData.rows || []);
      setData(newSheetData.data || []);
      setFilters(newSheetData.filters || []);
      setAggregations(newSheetData.aggregations || []);
      setDataFilters(newSheetData.dataFilters || {});
      setGeneratedSqlQuery(newSheetData.generatedSqlQuery || "");
      setComputedColumns(newSheetData.calculatedColumns || []); // Load calculated columns

      // If the saved scenario already has a materialized table, use it as‑is
      // instead of re-running the SQL. This ensures that after you run a query
      // and save, the exact latest table is what gets restored from storage.
      if (
        Array.isArray(newSheetData.queryResultTableData) &&
        newSheetData.queryResultTableData.length > 0
      ) {
        setQueryResultTableData(newSheetData.queryResultTableData);
        return;
      }

      // Otherwise, if there's a generated SQL query but no stored table yet,
      // auto-execute it once to populate the table.
      if (
        newSheetData.generatedSqlQuery &&
        newSheetData.generatedSqlQuery.trim()
      ) {
        try {
          const sqlQuery = { sql: newSheetData.generatedSqlQuery.trim() };
          // Prefer the saved query's user_db_id; fall back to stored ids.
          // For postgres, backend expects 0.
          const userDbId =
            newSheetData.dbtype === "postgres"
              ? 0
              : ((newSheetData.user_db_id !== null &&
                  newSheetData.user_db_id !== undefined &&
                  String(newSheetData.user_db_id) !== "0")
                  ? newSheetData.user_db_id
                  : (StorageHelper.get("snowflake_datasource_id") ??
                      StorageHelper.get("selected_user_db_id")));

          const rowFields = (newSheetData.rows || [])
            .map((r) => r.name)
            .filter(Boolean);
          const columnFields = (newSheetData.columns || [])
            .map((c) => c.name)
            .filter(Boolean);
          const dataFields = [
            ...(newSheetData.data || []).map((d) => d.name).filter(Boolean),
            ...(newSheetData.calculatedColumns || [])
              .map((cc) => cc.columnName)
              .filter(Boolean),
          ];

          const runResult = await runQueryApi(
            sqlQuery,
            Number(userId),
            Number(userDbId),
            columnFields,
            rowFields,
            dataFields,
            newSheetData.dbtype || getCurrentDbType()
          );

          if (runResult.success) {
            const output = transformPivotTableData(runResult.data);
            setQueryResultTableData(output);
          } else {
            console.error(
              "Saved query auto-execute failed:",
              runResult?.error || runResult?.message
            );
          }
        } catch (error) {
          console.error("Error executing saved query:", error);
        }
      } else {
        // No stored table and no SQL to run – clear any stale table view.
        setQueryResultTableData(null);
      }
    },
    [
      sheets,
      activeSheetIndex,
      setSheets,
      setActiveSheetIndex,
      setColumns,
      setRows,
      setData,
      setFilters,
      setAggregations,
      setDataFilters,
      setQueryResultTableData,
      setGeneratedSqlQuery,
      executeCustomSql,
      saveSheetsToStorage,
      saveActiveSheetIndexToStorage,
    ]
  );

  const deleteSheet = (sheetIndex) => {
    if (sheets.length <= 1) return; // Don't delete the last sheet

    const newSheets = sheets.filter((_, index) => index !== sheetIndex);
    setSheets(newSheets);

    // Adjust active sheet index if necessary
    let newActiveIndex = activeSheetIndex;
    if (activeSheetIndex >= sheetIndex) {
      newActiveIndex = Math.max(0, activeSheetIndex - 1);
      setActiveSheetIndex(newActiveIndex);
    }

    saveSheetsToStorage(newSheets);
    saveActiveSheetIndexToStorage(newActiveIndex);
   if (newActiveIndex >= 0 && newActiveIndex < newSheets.length) {
      const newActiveSheet = newSheets[newActiveIndex];
      if (newActiveSheet) {
        const sheetData = newActiveSheet.data || {};
        setColumns(sheetData.columns || []);
        setRows(sheetData.rows || []);
        setData(sheetData.data || []);
        setFilters(sheetData.filters || []);
        setAggregations(sheetData.aggregations || []);
        setDataFilters(sheetData.dataFilters || {});
        setQueryResultTableData(sheetData.queryResultTableData);
        setGeneratedSqlQuery(sheetData.generatedSqlQuery || "");
        setComputedColumns(sheetData.calculatedColumns || []);
        setUploadedFlat([]);
        setUploadedHeaders([]);
      }
    }
  };

  const renameSheet = (sheetIndex, newName) => {
    if (!newName.trim()) return;

    // Limit sheet name to 50 characters
    const trimmedName = newName.trim().substring(0, 50);

   const existingLower = new Set(
      sheets
        .map((s, idx) => (idx === sheetIndex ? null : s?.name))
        .filter((n) => typeof n === "string")
        .map((n) => n.trim().toLowerCase())
    );

    const makeUniqueName = (desired) => {
      const desiredLower = desired.trim().toLowerCase();
      if (!existingLower.has(desiredLower)) return desired;

      const m = /^(.*?)(?:\s*\((\d+)\))?$/.exec(desired.trim());
      const baseRaw = (m?.[1] || desired).trim();

      let i = 1;
      while (true) {
        const suffix = ` (${i})`;
        const maxBaseLen = Math.max(1, 50 - suffix.length);
        const base = baseRaw.substring(0, maxBaseLen).trimEnd();
        const candidate = `${base}${suffix}`;
        if (!existingLower.has(candidate.toLowerCase())) return candidate;
        i += 1;
      }
    };

    const finalName = makeUniqueName(trimmedName);
    const newSheets = sheets.map((sheet, index) =>
      index === sheetIndex
        ? { ...sheet, name: finalName, modified: true }
        : sheet
    );
    setSheets(newSheets);
    saveSheetsToStorage(newSheets);
  };

  const duplicateSheet = (sheetIndex) => {
    const sheetToDuplicate = sheets[sheetIndex];
    const originalName = sheetToDuplicate.name || `Task-${sheetIndex + 1}`;
    // Limit sheet name to 50 characters
    const duplicateName = `${originalName} (copy)`.substring(0, 50);
    const newSheet = {
      id: `sheet-${Date.now()}`,
      name: duplicateName,
      modified: false,
      saved_query_id: null, // Duplicated sheets are always editable
      owner_id: null,
      last_saved_query: "",
      data: {
        ...sheetToDuplicate.data,
        // Deep copy the data to avoid reference issues
        columns: [...sheetToDuplicate.data.columns],
        rows: [...sheetToDuplicate.data.rows],
        data: [...sheetToDuplicate.data.data],
        filters: [...sheetToDuplicate.data.filters],
        aggregations: [...sheetToDuplicate.data.aggregations],
        dataFilters: { ...sheetToDuplicate.data.dataFilters },
        dbtype: sheetToDuplicate.data.dbtype || getCurrentDbType(),
      },
    };

    const newSheets = [...sheets];
    newSheets.splice(sheetIndex + 1, 0, newSheet);
    setSheets(newSheets);
    setActiveSheetIndex(sheetIndex + 1);
    saveSheetsToStorage(newSheets);
    saveActiveSheetIndexToStorage(sheetIndex + 1);
  };

  const reorderSheets = (dragIndex, dropIndex) => {
    // Validate indices
    if (
      dragIndex < 0 ||
      dragIndex >= sheets.length ||
      dropIndex < 0 ||
      dropIndex >= sheets.length
    ) {
      return;
    }

    // First, save the current sheet's data
    if (activeSheetIndex >= 0 && activeSheetIndex < sheets.length) {
      const currentSheet = sheets[activeSheetIndex];
      if (currentSheet) {
        currentSheet.data = {
          columns: columns || [],
          rows: rows || [],
          data: data || [],
          filters: filters || [],
          aggregations: aggregations || [],
          dataFilters: dataFilters || {},
          queryResultTableData,
          generatedSqlQuery: generatedSqlQuery || "",
          calculatedColumns: computedColumns || [], // Save calculated columns
        };
      }
    }

    // Create a deep copy of the sheets array
    const newSheets = sheets.map((sheet) => ({
      ...sheet,
      data: sheet.data ? { ...sheet.data } : {},
      name: sheet.name || `Task-${sheets.indexOf(sheet) + 1}`,
    }));

    // Remove the dragged sheet and insert it at the new position
    const [draggedSheet] = newSheets.splice(dragIndex, 1);
    newSheets.splice(dropIndex, 0, draggedSheet);

    // Update sheet names based on their new positions if they're using default names
    newSheets.forEach((sheet, index) => {
      if (!sheet.name || sheet.name.startsWith("Task-")) {
        sheet.name = `Task-${index + 1}`;
      }
    });

    // Update active sheet index if needed
    let newActiveIndex = activeSheetIndex;
    if (activeSheetIndex === dragIndex) {
      // If we're dragging the active sheet, it moves with the drag
      newActiveIndex = dropIndex;
    } else if (dragIndex < dropIndex) {
      // Moving a sheet forward
      if (activeSheetIndex > dragIndex && activeSheetIndex <= dropIndex) {
        newActiveIndex--;
      }
    } else {
      // Moving a sheet backward
      if (activeSheetIndex >= dropIndex && activeSheetIndex < dragIndex) {
        newActiveIndex++;
      }
    }

    // Update sheets state and storage
    setSheets(newSheets);
    saveSheetsToStorage(newSheets);

    // Update active index and storage
    setActiveSheetIndex(newActiveIndex);
    saveActiveSheetIndexToStorage(newActiveIndex);

    // Load the data for the new active sheet
    if (newActiveIndex >= 0 && newActiveIndex < newSheets.length) {
      const newActiveSheet = newSheets[newActiveIndex];
      if (newActiveSheet) {
        // Ensure data object exists
        const sheetData = newActiveSheet.data || {};

        // Set all data with proper defaults
        setColumns(Array.isArray(sheetData.columns) ? sheetData.columns : []);
        setRows(Array.isArray(sheetData.rows) ? sheetData.rows : []);
        setData(Array.isArray(sheetData.data) ? sheetData.data : []);
        setFilters(Array.isArray(sheetData.filters) ? sheetData.filters : []);
        setAggregations(
          Array.isArray(sheetData.aggregations) ? sheetData.aggregations : []
        );
        setDataFilters(
          typeof sheetData.dataFilters === "object" ? sheetData.dataFilters : {}
        );
        setQueryResultTableData(sheetData.queryResultTableData);
        setGeneratedSqlQuery(
          typeof sheetData.generatedSqlQuery === "string"
            ? sheetData.generatedSqlQuery
            : ""
        );
        setComputedColumns(
          Array.isArray(sheetData.calculatedColumns)
            ? sheetData.calculatedColumns
            : []
        ); // Load calculated columns
      } else {
      }
    } else {
    }
  };

  const switchToSheet = (sheetIndex) => {
    if (sheetIndex >= 0 && sheetIndex < sheets.length) {
      setActiveSheetIndex(sheetIndex);
      saveActiveSheetIndexToStorage(sheetIndex);

      // Load the sheet's data into the current state
      const sheet = sheets[sheetIndex];
      if (sheet) {
        // Ensure we're setting empty arrays/objects for new sheets
        setColumns(sheet.data?.columns || []);
        setRows(sheet.data?.rows || []);
        setData(sheet.data?.data || []);
        setFilters(sheet.data?.filters || []);
        setAggregations(sheet.data?.aggregations || []);
        setDataFilters(sheet.data?.dataFilters || {});
        setQueryResultTableData(sheet.data?.queryResultTableData || null);
        setGeneratedSqlQuery(sheet.data?.generatedSqlQuery || "");
        setComputedColumns(sheet.data?.calculatedColumns || []); // Load calculated columns
        setUploadedFlat([]);
        setUploadedHeaders([]);
      } else {
        // Clear all data if sheet is not found or is new
        setColumns([]);
        setRows([]);
        setData([]);
        setFilters([]);
        setAggregations([]);
        setDataFilters({});
        setQueryResultTableData(null);
        setGeneratedSqlQuery("");
        setComputedColumns([]); // Clear calculated columns
        setUploadedFlat([]);
        setUploadedHeaders([]);
      }
    }
  };

  const saveCurrentSheetData = () => {
    const newSheets = sheets.map((sheet, index) =>
      index === activeSheetIndex
        ? {
            ...sheet,
            data: {
              columns,
              rows,
              data,
              filters,
              aggregations,
              dataFilters,
              queryResultTableData,
              generatedSqlQuery,
              calculatedColumns: computedColumns || [], // Save calculated columns
            },
            modified: true,
          }
        : sheet
    );
    setSheets(newSheets);
    // Debounced write so heavy data doesn't block UI; add/delete/rename still save immediately
    saveSheetsToStorageDebounced(newSheets);
  };

  // Load active sheet data when component mounts
  useEffect(() => {
    if (
      sheets.length > 0 &&
      activeSheetIndex >= 0 &&
      activeSheetIndex < sheets.length
    ) {
      const activeSheet = sheets[activeSheetIndex];
      if (activeSheet && activeSheet.data) {
        // Use a single batch update to set all state at once
        // This prevents multiple re-renders and ensures all state is set before auto-save runs
        setColumns(activeSheet.data.columns || []);
        setRows(activeSheet.data.rows || []);
        setData(activeSheet.data.data || []);
        setFilters(activeSheet.data.filters || []);
        setAggregations(activeSheet.data.aggregations || []);
        setDataFilters(activeSheet.data.dataFilters || {});
        setQueryResultTableData(activeSheet.data.queryResultTableData);
        setGeneratedSqlQuery(activeSheet.data.generatedSqlQuery || "");
        setComputedColumns(activeSheet.data.calculatedColumns || []); // Load calculated columns

        // Mark initial mount as complete after state updates are flushed
        // Use requestAnimationFrame to ensure state updates are processed
        requestAnimationFrame(() => {
          setTimeout(() => {
            isInitialMount.current = false;
          }, 50);
        });
      } else {
        // If no sheet data, mark as complete immediately
        isInitialMount.current = false;
      }
    } else {
      // If no sheets, mark as complete immediately
      isInitialMount.current = false;
    }
  }, []); // Only run on mount

  // Load active sheet data when activeSheetIndex or sheets change (e.g. after hydrate from storage)
  useEffect(() => {
    if (
      sheets.length > 0 &&
      activeSheetIndex >= 0 &&
      activeSheetIndex < sheets.length
    ) {
      const sheet = sheets[activeSheetIndex];
      if (sheet) {
        skipNextAutoSaveRef.current = true;
        setColumns(sheet.data?.columns || []);
        setRows(sheet.data?.rows || []);
        setData(sheet.data?.data || []);
        setFilters(sheet.data?.filters || []);
        setAggregations(sheet.data?.aggregations || []);
        setDataFilters(sheet.data?.dataFilters || {});
        setQueryResultTableData(sheet.data?.queryResultTableData ?? null);
        setGeneratedSqlQuery(sheet.data?.generatedSqlQuery || "");
        setComputedColumns(sheet.data?.calculatedColumns || []);
      }
    }
  }, [activeSheetIndex, sheets]);

  // Auto-save current sheet data when columns/rows/data etc. change (skip one run after loading sheet to avoid loop)
  useEffect(() => {
    if (isInitialMount.current) return;
    if (skipNextAutoSaveRef.current) {
      skipNextAutoSaveRef.current = false;
      return;
    }
    if (sheets.length > 0) {
      saveCurrentSheetData();
    }
  }, [
    columns,
    rows,
    data,
    filters,
    aggregations,
    dataFilters,
    queryResultTableData,
    generatedSqlQuery,
    computedColumns,
  ]);

  const downloadQuery = async (sql, downloadType, format = "csv") => {
    let sqlStr = sql;
    if (typeof sql === "object" && sql !== null && "sql" in sql) {
      sqlStr = sql.sql;
    }
    if (typeof sqlStr !== "string") {
      return { success: false, error: "No SQL provided" };
    }
    if (!sqlStr.trim()) return { success: false, error: "No SQL provided" };
    try {
      const userDbId =
        StorageHelper.get("snowflake_datasource_id") ||
        sheets[activeSheetIndex]?.data?.userDbId ||
        userId;

      const dbType =
        sheets[activeSheetIndex]?.data?.dbtype || getCurrentDbType();
      const rowFields = rows.map((r) => r.name).filter(Boolean);
      const columnFields = columns.map((c) => c.name).filter(Boolean);
      const dataFields = [
        ...data.map((d) => d.name).filter(Boolean),
        ...(computedColumns || []).map((cc) => cc.columnName).filter(Boolean),
      ];

      const result = await downloadQueryApi(
        { sql: sqlStr },
        Number(userId),
        Number(userDbId),
        columnFields,
        rowFields,
        dataFields,
        dbType,
        format,
        downloadType
      );
      return result;
    } catch (error) {
      return { success: false, error: error.message || "Download failed" };
    }
  };

  // Async download function
  const downloadQueryAsync = async (sql, downloadType, format = "csv") => {
    let sqlStr = sql;
    let tabNameFromPayload;
    if (typeof sql === "object" && sql !== null && "sql" in sql) {
      sqlStr = sql.sql;
      tabNameFromPayload = sql.tabName;
    }
    if (typeof sqlStr !== "string") {
      return { success: false, error: "No SQL provided" };
    }
    if (!sqlStr.trim()) return { success: false, error: "No SQL provided" };

    try {
      const userDbId =
        StorageHelper.get("snowflake_datasource_id") ||
        StorageHelper.get("selected_user_db_id");
      const dbType =
        sheets[activeSheetIndex]?.data?.dbtype || getCurrentDbType();
      const rowFields = rows.map((r) => r.name).filter(Boolean);
      const columnFields = columns.map((c) => c.name).filter(Boolean);
      const dataFields = [
        ...data.map((d) => d.name).filter(Boolean),
        ...(computedColumns || []).map((cc) => cc.columnName).filter(Boolean),
      ];
      const tabName =
        tabNameFromPayload ||
        sheets?.[activeSheetIndex]?.name ||
        queryName ||
        `Task-${activeSheetIndex + 1}`;

      const result = await startAsyncDownload(
        { sql: sqlStr },
        Number(userId),
        Number(userDbId),
        columnFields,
        rowFields,
        dataFields,
        dbType,
        format,
        downloadType,
        tabName
      );

      return result;
    } catch (error) {
      return {
        success: false,
        error: error.message || "Async download failed",
      };
    }
  };

  // Function to compute formula value for a single row
  const computeFormulaValue = (formula, rowData, availableColumns) => {
    try {
      // Remove the leading = if present
      let formulaStr = formula.trim();
      if (formulaStr.startsWith("=")) {
        formulaStr = formulaStr.substring(1);
      }

      // Get all column names from the row data
      const rowKeys = Object.keys(rowData);

      // Create a mapping of column names (with underscores) to their values
      const columnValueMap = {};
      rowKeys.forEach((key) => {
        const sanitizedKey = key.replace(/\s+/g, "_");
        columnValueMap[sanitizedKey] = rowData[key];
        // Also support the original key name
        columnValueMap[key] = rowData[key];
      });

      // Replace column references in the formula with their values
      // This handles cases like Overall_Budgeted_Hours, Overall Budgeted Hours, etc.
      let evaluatedFormula = formulaStr;
      const columnNames = [...new Set([...rowKeys, ...availableColumns])];

      // Sort by length (longest first) to avoid partial replacements
      columnNames.sort((a, b) => b.length - a.length);

      columnNames.forEach((colName) => {
        const sanitizedColName = colName.replace(/\s+/g, "_");
        const value = rowData[colName] ?? columnValueMap[sanitizedColName];

        if (value !== undefined && value !== null) {
          // Replace column name (with word boundaries) in the formula
          // Escape special regex characters
          const escapedColName = sanitizedColName.replace(
            /[.*+?^${}()|[\]\\]/g,
            "\\$&"
          );
          const regex = new RegExp(`\\b${escapedColName}\\b`, "g");

          // Replace with numeric value or quoted string
          if (typeof value === "number") {
            evaluatedFormula = evaluatedFormula.replace(
              regex,
              value.toString()
            );
          } else {
            // For strings, wrap in quotes
            evaluatedFormula = evaluatedFormula.replace(
              regex,
              `"${String(value).replace(/"/g, '\\"')}"`
            );
          }
        }
      });

      // Create a context object with ALL formulajs functions and Math
      // Dynamically include all formulajs functions
      const context = {
        // Math object
        Math: Math,
        // Math constants
        PI: Math.PI,
        E: Math.E,
        // Include all formulajs functions dynamically
        ...Object.keys(formulajs).reduce((acc, funcName) => {
          if (typeof formulajs[funcName] === "function") {
            acc[funcName] = formulajs[funcName];
          }
          return acc;
        }, {}),
        // Additional helper functions
        FLOOR: (x) => Math.floor(x),
        CEIL: (x) => Math.ceil(x),
      };

      // Use Function constructor to evaluate the formula
      // This approach allows dynamic evaluation of formulas
      try {
        const funcBody = `"use strict"; return (${evaluatedFormula});`;
        const func = new Function(...Object.keys(context), funcBody);
        const result = func(...Object.values(context));

        // Handle NaN and Infinity
        if (result === null || result === undefined) {
          return null;
        }

        if (
          typeof result === "number" &&
          (isNaN(result) || !isFinite(result))
        ) {
          return null;
        }

        return result;
      } catch (evalError) {
        console.error(
          "Formula evaluation error:",
          evalError,
          "Formula:",
          evaluatedFormula
        );
        return null;
      }
    } catch (error) {
      console.error("Error computing formula:", error);
      return null;
    }
  };

  // Function to add a computed column
  const addComputedColumn = async (columnName, formula, tableName, dataType) => {
    const newComputedColumn = {
      columnName: columnName,
      formula: formula,
      tableName: tableName,
      // Let caller decide type; if unknown, this may be undefined/null
      dataType: dataType,
    };

    // Optimistically add so the UI (Data zone) shows it immediately.
    // If SQL generation/run fails, we'll roll it back and surface the error.
    const prevArray = Array.isArray(computedColumns) ? computedColumns : [];
    const exists = prevArray.some((c) => c?.columnName === newComputedColumn.columnName);
    const optimisticColumns = exists
      ? prevArray.map((c) =>
          c?.columnName === newComputedColumn.columnName ? newComputedColumn : c
        )
      : [...prevArray, newComputedColumn];

    setComputedColumns(optimisticColumns);

    // Persist into active sheet immediately so it survives tab switches/reload
    const currentSheet = sheets[activeSheetIndex];
    if (currentSheet) {
      const updatedSheets = sheets.map((sheet, index) =>
        index === activeSheetIndex
          ? {
              ...sheet,
              data: {
                ...sheet.data,
                calculatedColumns: optimisticColumns,
              },
              modified: true,
            }
          : sheet
      );
      setSheets(updatedSheets);
      saveSheetsToStorage(updatedSheets);
    }

    // Track as pending so failure can be attributed to this column
    setPendingCalculatedColumn(newComputedColumn);

    // Clear any previous failed column state
    setFailedCalculatedColumn(null);
    setSqlGenError("");

    // Automatically trigger SQL generation to test the column
    // Pass the pending column directly to avoid closure issues
    setTimeout(async () => {
      await handleGenerateSQL(optimisticColumns, newComputedColumn);
    }, 0);
  };

  // Calculated columns are now computed in the backend SQL query
  // We just use the queryResultTableData directly from the API response
  // This function is kept for backward compatibility but returns the data as-is
  const getTableDataWithComputedColumns = useMemo(() => {
    // Backend already computes calculated columns, so return data directly
    return queryResultTableData;
  }, [queryResultTableData]);

  // Function to remove a computed column
  const removeComputedColumn = async (columnName) => {
    // Remove from state and get updated array
    const updatedColumns = computedColumns.filter(
      (col) => col.columnName !== columnName
    );

    // Only proceed if a column was actually removed
    if (updatedColumns.length === computedColumns.length) {
      return; // No column was removed
    }
    // Update state immediately
    setComputedColumns(updatedColumns);

    // Do NOT mutate queryResultTableData here. Table and SQL stay as-is until
    // user clicks "Run Query"; then the new query runs and table updates to match.

    // Persist only the updated calculatedColumns list; keep existing table/SQL until Generate
    const currentSheet = sheets[activeSheetIndex];
    if (currentSheet) {
      const updatedSheets = sheets.map((sheet, index) =>
        index === activeSheetIndex
          ? {
              ...sheet,
              data: {
                ...sheet.data,
                calculatedColumns: updatedColumns,
                // Leave queryResultTableData unchanged; it will update on next Run Query
              },
              modified: true,
            }
          : sheet
      );
      setSheets(updatedSheets);
      saveSheetsToStorage(updatedSheets);
    }
  };

  // Function to update a computed column
  const updateComputedColumn = async (
    oldColumnName,
    newColumnName,
    newFormula
  ) => {
    // Find the column being updated to get its tableName
    const oldColumn = computedColumns.find(col => col.columnName === oldColumnName);
    if (!oldColumn) return;

    // Store as pending (the updated version)
    const updatedColumn = {
      columnName: newColumnName,
      formula: newFormula,
      tableName: oldColumn.tableName,
      // Preserve any explicit datatype that may already exist on the column
      dataType: oldColumn.dataType || oldColumn.datatype || oldColumn.type || "VARCHAR",
      __isUpdate: true,
      __oldColumnName: oldColumnName
    };
    setPendingCalculatedColumn(updatedColumn);

    // Optimistically update the column name & formula in state so that
    // all UI surfaces (Data zone chip list, headers, etc.) immediately
    // reflect the new name. If SQL generation fails, the failure handler
    // will clean this up and surface the error.
    setComputedColumns((prev) => {
      const prevArray = Array.isArray(prev) ? prev : [];
      return prevArray.map((col) => {
        if (col.columnName === oldColumnName) {
          return {
            ...col,
            columnName: newColumnName,
            formula: newFormula,
          };
        }
        return col;
      });
    });

    // Persist the optimistic update into the active sheet so the renamed
    // column is also reflected after tab switches / refresh.
    const currentSheetForUpdate = sheets[activeSheetIndex];
    if (currentSheetForUpdate) {
      const updatedSheets = sheets.map((sheet, index) =>
        index === activeSheetIndex
          ? {
              ...sheet,
              data: {
                ...(sheet.data || {}),
                calculatedColumns: (sheet.data?.calculatedColumns || []).map(
                  (col) =>
                    col.columnName === oldColumnName
                      ? {
                          ...col,
                          columnName: newColumnName,
                          formula: newFormula,
                        }
                      : col
                ),
              },
              modified: true,
            }
          : sheet
      );
      setSheets(updatedSheets);
      saveSheetsToStorage(updatedSheets);
    }

    // Clear any previous failed column state
    setFailedCalculatedColumn(null);
    setSqlGenError("");

    // Prepare test array with the updated column
    const updatedComputedColumns = computedColumns.map((col) => {
      if (col.columnName === oldColumnName) {
        return {
          columnName: newColumnName,
          formula: newFormula,
          tableName: col.tableName,
        };
      }
      return col;
    });

    // Automatically trigger SQL generation to test the update
    // Pass the pending column directly to avoid closure issues
    setTimeout(async () => {
      await handleGenerateSQL(updatedComputedColumns, updatedColumn);
    }, 0);
  };

  return {
    columns,
    setColumns,
    rows,
    setRows,
    data,
    setData,
    dragOver,
    setDragOver,
    tableLoading,
    setTableLoading,
    tableData,
    setTableData,
    expandedMap,
    setExpandedMap,
    uploadedFlat,
    setUploadedFlat,
    uploadedHeaders,
    setUploadedHeaders,
    uploadDialogOpen,
    setUploadDialogOpen,
    columnTypes,
    setColumnTypes,
    uploadedFiles,
    setUploadedFiles,
    dataFilters,
    setDataFilters,
    filterDropdownOpen,
    setFilterDropdownOpen,
    filterMenuAnchor,
    setFilterMenuAnchor,
    filterMenuCol,
    setFilterMenuCol,
    filterSearch,
    setFilterSearch,
    filterOptions,
    setFilterOptions,
    filterLoading,
    setFilterLoading,
    filterError,
    setFilterError,
    filterCondition,
    setFilterCondition,
    pendingFilter,
    setPendingFilter,
    pendingValuesByCondition,
    setPendingValuesByCondition,
    queryResultTableData,
    setQueryResultTableData,
    generatedSqlQuery,
    setGeneratedSqlQuery,
    aggregations,
    setAggregations,
    aggDropdownCol,
    setAggDropdownCol,
    aggDropdownValue,
    setAggDropdownValue,
    filters,
    setFilters,
    filterIconRefs,
    AGG_FUNCTIONS,
    uploadFileToMinio,
    uploadLoading,
    uploadError,
    uploadData,
    createQueryApi,
    sqlLoading,
    sqlError,
    sqlData,
    getRecordsByColumnNameApi,
    getRecordsLoading,
    getRecordsError,
    getRecordsData,
    runQueryApi,
    runQueryLoading,
    runQueryError,
    runQueryData,
    downloadQuery,
    downloadQueryAsync,
    getColumnDateDetectionApi,
    getColumnDateDetectionLoading,
    getChartMetadataApi,
    getChartMetadataLoading,
    getChartDataApi,
    getChartDataLoading,
    getChartDataError,
    schemaInfo,
    user,
    userId,
    formatNumber,
    flattenData,
    getAllColumns,
    getValue,
    getCurrentDbType,
    handleFileChange,
    handleDrop,
    FILTER_CONDITIONS,
    CONDITION_NAME_MAP,
    handleGenerateSQL,
    executeCustomSql,
    transformPivotTableData,
    getTableHeaders,
    sqlGenError,
    setSqlGenError,
    saveQueryDialogOpen,
    setSaveQueryDialogOpen,
    queryName,
    setQueryName,
    saveQueryError,
    setSaveQueryError,
    saveQuerySuccess,
    setSaveQuerySuccess,
    editSaveSuccessAlert,
    setEditSaveSuccessAlert,
    addSavedQueryApi,
    addSavedQueryLoading,
    addSavedQueryError,
    addSavedQueryData,
    handleSaveQuery,
    saveQuery,
    closeSaveQueryDialog,
    modal,
    setModal,
    // Sheet management
    sheets,
    setSheets,
    activeSheetIndex,
    setActiveSheetIndex,
    addSheet,
    addSheetWithData,
    deleteSheet,
    renameSheet,
    duplicateSheet,
    reorderSheets,
    switchToSheet,
    saveCurrentSheetData,
    // StorageHelper functions
    saveSheetsToStorage,
    saveActiveSheetIndexToStorage,
    clearSheetsStorage,
    resetAllSheets,
    // Computed columns
    computedColumns,
    setComputedColumns,
    addComputedColumn,
    removeComputedColumn,
    updateComputedColumn,
    getTableDataWithComputedColumns,
    // Failed calculated column tracking
    failedCalculatedColumn,
    setFailedCalculatedColumn,
  };
}
